import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Modal, message
 } from "antd";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import ChatWindow from "../components/ChatWindow";
import Resizer from "../components/Resizer";
import ConfigEditor from "../components/ConfigEditor";
import SettingsPanel from "../components/SettingsPanel";
import TeamManagementModal from "../components/TeamManagementModal";
import SubscriptionModal from "../components/SubscriptionModal";
import { createMessagingClient } from "../api/messagingClient";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSocket } from "../hooks/useSocket";
import notifyIncoming from "../assets/sounds/notify-incoming.wav";
import notifySent from "../assets/sounds/notify-sent.wav";
import notifyStatus from "../assets/sounds/notify-status.wav";
import styles from "./ChatApp.module.css";

const SUBSCRIPTION_REQUIRED_MESSAGE =
  "You don't have an active subscription. Messaging Service can't be used";

export default function ChatApp({
  onLogout,
  openConfigModalOnce = false,
  onOpenConfigModalConsumed,
  userId,
  phoneId
}) {
  const [currentPhone, setCurrentPhone] = useState("");
  const [isLive, setIsLive] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionInitialParams, setSubscriptionInitialParams] = useState(null);
  const { token, decodeToken } = useAuth();
  const isMobile = useIsMobile(700);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [soundMuted, setSoundMuted] = useState(() => {
    try {
      return localStorage.getItem("zibuzzNotificationSoundMuted") === "true";
    } catch {
      return false;
    }
  });
  const audioRef = useRef({});
  const lastSoundRef = useRef({ newKey: "", newAt: 0, eventKey: "" });
  const chatUpdateSoundTimerRef = useRef(null);
  const subscriptionWarningShownRef = useRef(false);

  let isAdmin = true;
  let userProfile = null;
  let isWabaConnected = false;
  let isSubscriptionValid = false;

  try {
    if (token) {
      const decoded = decodeToken(token);
      userProfile = decoded?.UserProfile || null;
      const r = userProfile?.Role;
      isAdmin = r !== "team_member";
      isWabaConnected = !!userProfile?.wabaId;
      
      const plan = userProfile?.Plan;
      const pName = plan?.plan || plan?.Plan;
      const pExpiry = plan?.expiry || plan?.Expiry;
      
      if (pName && pExpiry) {
        isSubscriptionValid = new Date(pExpiry) > new Date();
      } else {
        isSubscriptionValid = false;
      }
    }
  } catch {
    isAdmin = true;
    isWabaConnected = false;
    isSubscriptionValid = false;
  }

  const socketEnabled =
    !!token &&
    isLive &&
    isWabaConnected &&
    isSubscriptionValid &&
    !!phoneId;

  const { lastMessage: lastSocketMessage } = useSocket(
    userId,
    phoneId,
    socketEnabled
  );

  const messaging = useMemo(() => {
    if (!token) return null;
    try {
      return createMessagingClient(token, { onAuthError: onLogout });
    } catch {
      return null;
    }
  }, [token, onLogout]);

  const showSubscriptionRequiredModal = useCallback(() => {
    Modal.warning({
      title: "Subscription Required",
      content: SUBSCRIPTION_REQUIRED_MESSAGE,
      okText: "OK",
    });
  }, []);

  const handleLiveToggle = useCallback((nextLive) => {
    if (nextLive && isWabaConnected && !isSubscriptionValid) {
      setIsLive(false);
      showSubscriptionRequiredModal();
      return;
    }

    setIsLive(nextLive);
  }, [isSubscriptionValid, isWabaConnected, showSubscriptionRequiredModal]);

  const loadContacts = useCallback(() => {
    if (!messaging) return Promise.resolve();

    return messaging
      .listThreads()
      .then((data) => {
        const threads = data?.threads || [];
        const mapped = threads.map((t) => {
          const phone = String(t.contact_number || "");
          const nameOnly =
            String(t.contact_name || "Unknown").trim() || "Unknown";
          const uc = Number(t.unread_count);
          const badge =
            uc > 0 ? ` ${uc} unread` : "";
          const fullLine = `${nameOnly} (${phone})${badge}`;
          const activityType = t.last_activity_type || "message";
          const activityPreview = activityType === "reaction"
            ? `${t.last_reaction_emoji || ""} reacted to a message`.trim()
            : t.last_message_preview || "";
          return {
            phone,
            nameOnly,
            fullLine,
            unread_count: uc,
            last_message_direction: t.last_message_direction || "",
            last_activity_type: activityType,
            last_reaction_emoji: t.last_reaction_emoji || "",
            activity_preview: activityPreview,
          };
        });
        setContacts(mapped);
      })
      .catch((err) => {
        message.error(err.message || "Could not load contacts");
      });
  }, [messaging]);

  const playNotificationSound = useCallback((kind) => {
    if (soundMuted) return;

    const src =
      kind === "sent"
        ? notifySent
        : kind === "status"
          ? notifyStatus
          : notifyIncoming;

    try {
      if (!audioRef.current[kind]) {
        audioRef.current[kind] = new Audio(src);
        audioRef.current[kind].preload = "auto";
      }

      const audio = audioRef.current[kind];
      audio.volume = kind === "sent" ? 0.28 : kind === "status" ? 0.24 : 0.32;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } catch {
      // Browsers can block audio before first user interaction.
    }
  }, [soundMuted]);

  useEffect(() => {
    try {
      localStorage.setItem("zibuzzNotificationSoundMuted", String(soundMuted));
    } catch {
      // Ignore private-mode/localStorage failures.
    }

    if (soundMuted && chatUpdateSoundTimerRef.current?.timer) {
      clearTimeout(chatUpdateSoundTimerRef.current.timer);
      chatUpdateSoundTimerRef.current = null;
    }
  }, [soundMuted]);

  useEffect(() => {
    if (!lastSocketMessage || soundMuted) return;

    const type = String(lastSocketMessage.type || "");
    const contact = String(lastSocketMessage.ContactNumber || "");
    const actionAt = String(lastSocketMessage.LastActionAt || "");
    const messageId = String(lastSocketMessage.WAMessageId || "");
    const status = String(lastSocketMessage.Status || "").toUpperCase();
    const direction = String(
      lastSocketMessage.Direction ||
        lastSocketMessage.LastMessageDirection ||
        ""
    ).toLowerCase();
    const eventKey = `${type}|${messageId || contact}|${actionAt}|${status}`;

    if (!eventKey.trim() || lastSoundRef.current.eventKey === eventKey) return;
    lastSoundRef.current.eventKey = eventKey;

    if (type === "STATUS_UPDATED") {
      playNotificationSound("status");
      return;
    }

    if (type === "REACTION_UPDATED") {
      return;
    }

    if (type === "NEW_MESSAGE") {
      const newKey = `${contact}|${actionAt}`;
      if (
        chatUpdateSoundTimerRef.current?.key === newKey &&
        chatUpdateSoundTimerRef.current?.timer
      ) {
        clearTimeout(chatUpdateSoundTimerRef.current.timer);
        chatUpdateSoundTimerRef.current = null;
      }

      lastSoundRef.current.newKey = newKey;
      lastSoundRef.current.newAt = Date.now();
      playNotificationSound(
        direction === "outbound" && status !== "SENT" ? "status" :
          direction === "outbound" ? "sent" : "incoming"
      );
      return;
    }

    if (type === "CHAT_UPDATE") {
      const updateKey = `${contact}|${actionAt}`;
      if (
        lastSoundRef.current.newKey === updateKey &&
        Date.now() - lastSoundRef.current.newAt < 1800
      ) {
        return;
      }

      if (chatUpdateSoundTimerRef.current?.timer) {
        clearTimeout(chatUpdateSoundTimerRef.current.timer);
      }

      chatUpdateSoundTimerRef.current = {
        key: updateKey,
        timer: setTimeout(() => {
          if (lastSoundRef.current.newKey !== updateKey) {
            playNotificationSound(direction === "outbound" ? "sent" : "incoming");
          }
          chatUpdateSoundTimerRef.current = null;
        }, 700),
      };
    }
  }, [lastSocketMessage, playNotificationSound, soundMuted]);

  useEffect(() => () => {
    if (chatUpdateSoundTimerRef.current?.timer) {
      clearTimeout(chatUpdateSoundTimerRef.current.timer);
    }
  }, []);

  useEffect(() => {
    if (!messaging || !isSubscriptionValid) {
      setIsLive(false);
      return;
    }
    setIsLive(true);
  }, [messaging, isSubscriptionValid]);

  useEffect(() => {
    if (!isLive || !messaging || !isSubscriptionValid) return;
    loadContacts();
  }, [isLive, loadContacts, messaging, isSubscriptionValid]);

  useEffect(() => {
    if (isWabaConnected && !isSubscriptionValid) {
      setIsLive(false);

      if (!subscriptionWarningShownRef.current) {
        subscriptionWarningShownRef.current = true;
        showSubscriptionRequiredModal();
      }
      return;
    }

    if (isSubscriptionValid) {
      subscriptionWarningShownRef.current = false;
    }
  }, [isSubscriptionValid, isWabaConnected, showSubscriptionRequiredModal]);

  useEffect(() => {
    if (!token) return;
    const d = decodeToken(token);
    if (!d?.exp || d.exp < Date.now() / 1000) {
      onLogout();
    }
  }, [token, decodeToken, onLogout]);

  useEffect(() => {
    if (!token) {
      onLogout();
    }
  }, [token, onLogout]);

  useEffect(() => {
    if (!openConfigModalOnce) return;
    setWhatsappModalOpen(true);
    onOpenConfigModalConsumed?.();
  }, [openConfigModalOnce, onOpenConfigModalConsumed]);

  const handleSelectContact = useCallback((phone) => {
    setCurrentPhone(phone);
  }, []);

  const handleOpenSubscription = useCallback((params) => {
    if (params?.selectedPlan && params?.selectedBillingCycle) {
      // Direct to payment with pre-selected plan and billing cycle
      setSubscriptionInitialParams({
        initialPlan: params.selectedPlan,
        initialValidity: params.selectedBillingCycle,
      });
    } else {
      // Open without pre-selection (show overview)
      setSubscriptionInitialParams(null);
    }
    setSubscriptionModalOpen(true);
  }, []);

  // Handle query parameters for auto-opening subscription modal
  useEffect(() => {
    const shouldOpenSubscription = searchParams.get('openSubscription') === 'true';
    const preSelectedPlan = searchParams.get('plan'); // 'solo' or 'galaxy'
    const preSelectedCycle = searchParams.get('cycle'); // 'monthly', 'quarterly', 'annual'

    if (shouldOpenSubscription) {
      handleOpenSubscription({
        selectedPlan: preSelectedPlan || undefined,
        selectedBillingCycle: preSelectedCycle || undefined,
      });

      // Clean up URL params
      searchParams.delete('openSubscription');
      searchParams.delete('plan');
      searchParams.delete('cycle');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, handleOpenSubscription]);

const lastProcessedIdRef = useRef(null);

useEffect(() => {
  if (!lastSocketMessage || lastSocketMessage.type !== "CHAT_UPDATE") return;

  console.log('[ChatApp] Processing real-time message for current contact');
  console.log(`[ChatApp] WebSocket Message: \n${JSON.stringify(lastSocketMessage, null, 2)}`)
  const msg = {
    contact_number: String(lastSocketMessage.ContactNumber || ""),
    contact_name: lastSocketMessage.ContactName || "Unknown",
    unread: lastSocketMessage.UnreadCount,
    lastActionAt: lastSocketMessage.LastActionAt,
    last_message_direction: lastSocketMessage.LastMessageDirection || "",
    last_activity_type: lastSocketMessage.LastActivityType || "message",
    last_reaction_emoji: lastSocketMessage.LastReactionEmoji || "",
    last_message_preview: lastSocketMessage.LastMessagePreview || "",
  };

  if (!msg.contact_number) return;

  // ✅ dedup key
  const uniqueKey = `${msg.contact_number}_${msg.lastActionAt}`;
  if (lastProcessedIdRef.current === uniqueKey) return;
  lastProcessedIdRef.current = uniqueKey;

  setContacts((prev) => {
    if (!Array.isArray(prev)) return [];

    // ✅ remove any existing entry for this contact (guarantees uniqueness)
    const filtered = prev.filter(
      (c) => String(c.phone) !== msg.contact_number
    );

    // ✅ compute unread
    const unread =
      msg.unread !== undefined && msg.unread !== null
        ? Number(msg.unread)
        : 0;

    const updatedContact = {
      phone: msg.contact_number,
      nameOnly: msg.contact_name,
      unread_count: unread,
      last_message_direction: msg.last_message_direction,
      last_activity_type: msg.last_activity_type,
      last_reaction_emoji: msg.last_reaction_emoji,
      activity_preview: msg.last_activity_type === "reaction"
        ? `${msg.last_reaction_emoji || ""} reacted to a message`.trim()
        : msg.last_message_preview,
      fullLine: `${msg.contact_name} (${msg.contact_number})${
        unread > 0 ? ` ✉ ${unread}` : ""
      }`,
    };

    // ✅ insert at top (latest activity first)
    return [updatedContact, ...filtered];
  });
}, [lastSocketMessage]);

  const totalUnread = contacts.reduce(
    (s, c) => s + (Number(c.unread_count) || 0),
    0
  );
  const hasGlobalUnread = totalUnread > 0;

  if (!token) {
    return null;
  }

  return (
    <div
      className={
        sidebarHidden
          ? `${styles.appWrapper} ${styles.sidebarHidden}`
          : styles.appWrapper
      }
    >
      <Sidebar
        currentPhone={currentPhone}
        onSelectContact={handleSelectContact}
        isLive={isLive}
        setIsLive={handleLiveToggle}
        disableLiveToggle={!messaging}
        isAdmin={isAdmin}
        isWabaConnected={isWabaConnected}
        onLogout={onLogout}
        onOpenTeam={() => setTeamModalOpen(true)}
        onOpenSubscription={handleOpenSubscription}
        onOpenSettings={() => setSettingsModalOpen(true)}
        mobileUnreadPulseLeft={
          isMobile && !sidebarHidden && hasGlobalUnread
        }
        contacts={contacts}
        userProfile={userProfile}
      />

      <Resizer />

      <div className={styles.chatMainPanel}>
        <ChatWindow
          currentPhone={currentPhone}
          isLive={isLive}
          isWabaConnected={isWabaConnected}
          isSubscriptionValid={isSubscriptionValid}
          messaging={messaging}
          contacts={contacts}
          sidebarHidden={sidebarHidden}
          onShowSidebar={() => setSidebarHidden(false)}
          onOpenConfigurations={() => setWhatsappModalOpen(true)}
          userProfile={userProfile}
          onLogout={onLogout}
          mobileUnreadPulseRight={
            isMobile && sidebarHidden && hasGlobalUnread
          }
          isSubscriptionRoute={location.pathname === '/subscription'}
          onOpenSubscription={handleOpenSubscription}
          onOpenTemplates={() => navigate("/templates")}
          lastSocketMessage={lastSocketMessage}
          soundMuted={soundMuted}
          onToggleSound={() => setSoundMuted((v) => !v)}
        />
      </div>

      <Modal
        title="Whatsapp Business Configuration"
        open={whatsappModalOpen}
        onCancel={() => setWhatsappModalOpen(false)}
        footer={null}
        destroyOnClose
        width={480}
      >
        <ConfigEditor
          visible={whatsappModalOpen}
          onSaved={() => {
            setWhatsappModalOpen(false);
          }}
          onCleared={() => {
            setWhatsappModalOpen(false);
          }}
          showClear
        />
      </Modal>

      <Modal
        title="Settings"
        open={settingsModalOpen}
        onCancel={() => setSettingsModalOpen(false)}
        footer={null}
        destroyOnClose
        width={440}
      >
        <SettingsPanel
          token={token}
          isTeamMember={!isAdmin}
          onLogout={() => {
            setSettingsModalOpen(false);
            onLogout();
          }}
        />
      </Modal>

      <TeamManagementModal
        open={teamModalOpen}
        onCancel={() => setTeamModalOpen(false)}
        token={token}
      />

      <SubscriptionModal
        open={subscriptionModalOpen}
        onCancel={() => {
          setSubscriptionModalOpen(false);
          setSubscriptionInitialParams(null);
        }}
        userProfile={userProfile}
        initialPlan={subscriptionInitialParams?.initialPlan}
        initialValidity={subscriptionInitialParams?.initialValidity}
      />
    </div>
  );
}
